import classNames from 'classnames';
import React from 'react';

import SidebarLink from './SidebarLink/SidebarLink';
import useStyles from './styles';
import { useUserData } from '../../hooks/context/userUserData.hook';
import { useSideBarOpened } from '../../hooks/context/useSideBarOpened.hook';

export interface ISidebarMenu {
  id: number;
  name: string;
  link?: string;
  icon?: string;
  label: string;
  subMenu?: ISidebarMenu[];
}

export interface ISubMenuLinks extends ISidebarMenu {
  categoryId: number;
}

const menuConstants = {
  dashboard: 'Dashboard',
  titles: 'Titles',
  categories: 'Categories',
  contents: 'Contents',
  users: 'Users',
  allUsers: 'All Users',
  writers: 'Writers',
  products: 'Products',
  brands: 'Brands',
  allProducts: 'All Products',
  productTypes: 'Product Types',
  reports: 'Reports',
  tags: 'Tags',
  sitePages: 'Pages',
  siteCompliance: 'Complaince',
  sites: 'Sites',
  siteSettings: 'Settings',
};

const menuLinks: ISidebarMenu[] = [
  {
    id: 0,
    name: 'dashboard',
    label: menuConstants.dashboard,
    link: '/dashboard',
    icon: 'pie-chart',
  },
  {
    id: 1,
    name: 'titlesContent',
    label: menuConstants.titles,
    icon: 'file-text',
    subMenu: [
      {
        id: 16,
        name: 'titlesContent',
        label: menuConstants.titles,
        link: '/titlesContent',
      },
      {
        id: 15,
        name: 'categories',
        label: menuConstants.categories,
        link: '/categories',
      },
    ],
  },
  {
    id: 2,
    name: 'titlesWriter',
    label: menuConstants.titles,
    link: '/titlesWriter',
    icon: 'edit',
  },
  {
    id: 4,
    name: 'contents',
    label: menuConstants.contents,
    link: '/contents',
    icon: 'edit',
  },
  {
    id: 5,
    name: 'usersCategory',
    label: menuConstants.users,
    icon: 'user',
    subMenu: [
      { id: 6, name: 'users', label: menuConstants.allUsers, link: '/users' },
      {
        id: 7,
        name: 'writers',
        label: menuConstants.writers,
        link: '/writers',
      },
    ],
  },
  {
    id: 8,
    name: 'productsCategory',
    label: menuConstants.products,
    icon: 'appstore',
    subMenu: [
      {
        id: 9,
        name: 'brands',
        label: menuConstants.brands,
        link: '/brands',
        icon: 'block',
      },
      {
        id: 10,
        name: 'products',
        label: menuConstants.allProducts,
        link: '/products',
      },
      {
        id: 11,
        name: 'productTypes',
        label: menuConstants.productTypes,
        link: '/productTypes',
      },
    ],
  },
  {
    id: 12,
    name: 'reports',
    label: menuConstants.reports,
    link: '/reports',
    icon: 'stock',
  },
  {
    id: 13,
    name: 'tags',
    label: menuConstants.tags,
    link: '/tags',
    icon: 'tag',
  },
  {
    id: 14,
    name: 'sites',
    label: menuConstants.sites,
    icon: 'global',
    subMenu: [
      {
        id: 15,
        name: 'sitePages',
        label: menuConstants.sitePages,
        link: '/sitePages',
        icon: 'profile',
      },
      {
        id: 16,
        name: 'siteCompliance',
        label: menuConstants.siteCompliance,
        link: '/siteCompliance',
        icon: 'profile',
      },
      {
        id: 17,
        name: 'siteSettings',
        label: menuConstants.siteSettings,
        link: '/siteSettings',
        icon: 'settings',
      },
    ],
  },
];

const createStructure = (role: string) => {
  const structure: (ISidebarMenu | undefined)[] = [];

  const allowedMenus = (menus: string[]) => {
    const allowedMenus: (ISidebarMenu | undefined)[] = menus.map((menuName) => {
      const findMenuByName = menuLinks.find((menu) => menu.name === menuName);
      const subMenuLinks: ISubMenuLinks[] = [];

      menuLinks.forEach((menu) => {
        if (menu.subMenu) {
          menu.subMenu.forEach((submenu) => {
            return subMenuLinks.push({ ...submenu, categoryId: menu.id });
          });
        }
      });

      if (!findMenuByName) {
        const findSubMenuByName = subMenuLinks.find(
          (menu) => menu.name === menuName,
        );

        if (findSubMenuByName) {
          return findSubMenuByName;
        }
      }

      return findMenuByName;
    });

    structure.push(...allowedMenus);
  };

  switch (role) {
    case 'CONTENTDIRECTOR':
      allowedMenus([
        'dashboard',
        'titlesContent',
        'contents',
        'usersCategory',
        'productsCategoy',
        'reports',
        'sitePages',
      ]);
      break;
    case 'CONTENTMANAGER':
      allowedMenus([
        'dashboard',
        'titlesContent',
        'contents',
        'productsCategory',
        'reports',
        'sites',
      ]);
      break;
    case 'WRITER':
      allowedMenus(['titlesWriter']);
      break;
    case 'REVIEWER':
      allowedMenus(['titlesWriter']);
      break;
    case 'TRANSLATOR':
      allowedMenus(['titlesWriter']);
      break;
    case 'EDITOR':
      allowedMenus(['titlesWriter']);
      break;
    case 'ADMIN':
      allowedMenus([
        'dashboard',
        'titlesContent',
        'contents',
        'usersCategory',
        'productsCategory',
        'reports',
        'tags',
        'sites',
      ]);
      break;
    default:
      break;
  }
  return structure;
};

const Sidebar: React.FC = () => {
  const [{ role }] = useUserData();
  const structure = createStructure(role);
  const [isSidebarOpened] = useSideBarOpened();
  const classes = useStyles();

  return (
    <div
      className={classNames(classes.content, {
        [classes.menuOpen]: isSidebarOpened,
        [classes.menuClose]: !isSidebarOpened,
      })}
    >
      <div className={classes.toolbar} />
      <SidebarLink menuData={structure} />
    </div>
  );
};

export default Sidebar;
